// API Client for vendorboom.com
// Use relative URL for proxy, or environment variable, or default
const getApiBaseUrl = () => {
  // If VITE_API_URL is set and is a relative URL or localhost, use it
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl) {
    // If it's a relative URL (starts with /), use it as-is
    if (envUrl.startsWith('/')) {
      return envUrl;
    }
    // If it's localhost or 127.0.0.1, use relative URL for proxy
    if (envUrl.includes('localhost') || envUrl.includes('127.0.0.1')) {
      return 'http://localhost:8080/api/v1';
    }
    return envUrl;
  }
  // Default: use relative URL for proxy in development
  if (import.meta.env.DEV) {
    return 'http://localhost:8080/api/v1';
  }
  // Production default
  return 'https://api.vendorboom.com/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Load token from localStorage on initialization
    this.token = localStorage.getItem('auth_token');
  }

  setToken(token: string | null) {
    this.token = token;
    // Note: Auth0 manages its own token storage, but we keep a reference here
    // for immediate use in API calls. The token is refreshed from Auth0 when needed.
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  getToken(): string | null {
    // Return the in-memory token first, then check localStorage
    // The AuthContext will update this token from Auth0 when needed
    return this.token || localStorage.getItem('auth_token');
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: response.statusText || 'An error occurred',
        }));
        throw {
          message: errorData.message || 'An error occurred',
          code: errorData.code,
          status: response.status,
        } as ApiError;
      }

      // Handle empty responses
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      }
      return {} as T;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error) {
        throw error;
      }
      throw {
        message: error instanceof Error ? error.message : 'Network error',
        status: 0,
      } as ApiError;
    }
  }

  async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const queryString = params
      ? '?' + new URLSearchParams(params).toString()
      : '';
    return this.request<T>(`${endpoint}${queryString}`, {
      method: 'GET',
    });
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // File upload helper
  async uploadFile(
    endpoint: string,
    file: File,
    additionalData?: Record<string, any>
  ): Promise<{ url: string }> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const formData = new FormData();
    formData.append('file', file);
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
      });
    }

    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: response.statusText || 'Upload failed',
      }));
      throw {
        message: errorData.message || 'Upload failed',
        code: errorData.code,
        status: response.status,
      } as ApiError;
    }

    return await response.json();
  }
}

export const api = new ApiClient(API_BASE_URL);

// Auth API
export interface AuthResponse {
  user: {
    id: string;
    email: string;
    [key: string]: any;
  };
  token: string;
}

export interface SignUpRequest {
  email: string;
  password: string;
  role: 'contractor' | 'gc';
  full_name?: string;
}

export interface SignInRequest {
  email: string;
  password: string;
}

export const authApi = {
  signUp: async (data: SignUpRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/signup', data);
    api.setToken(response.token);
    return response;
  },

  signIn: async (data: SignInRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/signin', data);
    api.setToken(response.token);
    return response;
  },

  signOut: async (): Promise<void> => {
    await api.post('/auth/signout');
    api.setToken(null);
  },

  getCurrentUser: async (): Promise<AuthResponse['user'] | null> => {
    try {
      return await api.get<AuthResponse['user']>('/auth/me');
    } catch {
      return null;
    }
  },

  refreshToken: async (): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/refresh');
    api.setToken(response.token);
    return response;
  },
};

// Export types that were previously in supabase.ts
export type UserRole = 'contractor' | 'gc';

export interface Profile {
  id: string;
  role: UserRole;
  full_name: string | null;
  company_name?: string;
  phone?: string;
  location?: string;
  avatar_url?: string;
  bio?: string;
  trade_type?: string;
  trades?: string[];
  verification_status: 'unverified' | 'pending' | 'verified';
  rating: number;
  jobs_completed: number;
  onboardingCompleted: boolean; // API returns camelCase
  public_slug?: string;
  public_profile_enabled?: boolean;
  profile_views?: number;
  created_at: string;
  updated_at: string;
}

export interface Job {
  id: string;
  gc_id: string;
  title: string;
  description: string;
  trade_type: string;
  location: string;
  latitude?: number;
  longitude?: number;
  pay_rate: number;
  pay_type: 'hourly' | 'fixed' | 'daily';
  start_date?: string;
  duration?: string;
  insurance_required: boolean;
  verification_required: boolean;
  status: 'open' | 'closed' | 'filled';
  created_at: string;
  updated_at: string;
}

export interface Application {
  id: string;
  job_id: string;
  contractor_id: string;
  status: 'new' | 'contacted' | 'verified' | 'hired' | 'rejected';
  message?: string;
  proposed_rate?: number;
  created_at: string;
  updated_at: string;
}

export interface Certification {
  id: string;
  user_id: string;
  type: 'license' | 'certification' | 'skill';
  name: string;
  issuer?: string;
  number?: string;
  issue_date?: string;
  expiry_date?: string;
  document_url?: string;
  verified: boolean;
  created_at: string;
}

export interface InsurancePolicy {
  id: string;
  user_id: string;
  policy_type: 'liability' | 'workers_comp' | 'tools';
  provider: string;
  policy_number?: string;
  coverage_amount?: number;
  start_date: string;
  expiry_date: string;
  document_url?: string;
  status: 'active' | 'expiring' | 'expired';
  created_at: string;
}

export interface Notification {
  id: string;
  user_id: string;
  type: 'expiry' | 'job_match' | 'message' | 'application' | 'compliance';
  title: string;
  message: string;
  link?: string;
  read: boolean;
  created_at: string;
}

export interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  job_id?: string;
  content: string;
  read: boolean;
  created_at: string;
}

export interface TaxDocument {
  id: string;
  user_id: string;
  tax_id?: string;
  tax_id_type?: 'ein' | 'ssn';
  w9_document_url?: string;
  w9_verified: boolean;
  business_legal_name?: string;
  tax_classification?: 'individual' | 'sole_proprietor' | 'c_corp' | 's_corp' | 'partnership' | 'llc';
  w9_submission_date?: string;
  verification_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const maskTaxId = (taxId: string | undefined): string => {
  if (!taxId) return 'Not provided';

  const cleanId = taxId.replace(/[^\d]/g, '');

  if (cleanId.length === 9) {
    if (taxId.includes('-') && taxId.indexOf('-') < 3) {
      return `XX-XXX${cleanId.slice(-4)}`;
    }
    return `XXX-XX-${cleanId.slice(-4)}`;
  }

  return 'Invalid format';
};

export const formatTaxId = (taxId: string, type: 'ein' | 'ssn'): string => {
  const cleanId = taxId.replace(/[^\d]/g, '');

  if (type === 'ein' && cleanId.length === 9) {
    return `${cleanId.slice(0, 2)}-${cleanId.slice(2)}`;
  } else if (type === 'ssn' && cleanId.length === 9) {
    return `${cleanId.slice(0, 3)}-${cleanId.slice(3, 5)}-${cleanId.slice(5)}`;
  }

  return taxId;
};

export const validateTaxId = (taxId: string, type: 'ein' | 'ssn'): boolean => {
  const cleanId = taxId.replace(/[^\d]/g, '');
  return cleanId.length === 9;
};

